// Pure helpers for the IDX landing-page "subscribed cities" lock. Kept free of
// React/browser APIs so the gating logic is unit-testable on its own.

export interface SubscribedCityLike {
  name?: string;
  stateCode?: string;
}

// Title-case a slug segment, preserving pure-number segments (e.g. "4").
export const titleCaseSegment = (word: string): string =>
  /^\d+$/.test(word) ? word : word.charAt(0).toUpperCase() + word.slice(1);

// Parse a city slug into a display city name + state code, e.g.
// "colorado-springs-co" → { cityName: "Colorado Springs", stateCode: "co" }.
// State is returned as-is; callers upper-case it for display.
export const parseCitySlug = (
  citySlug: string | undefined
): { cityName: string; stateCode: string } => {
  const segments = (citySlug || "").split("-");
  const stateCode = segments[segments.length - 1] || "";
  const cityName = segments.slice(0, -1).map(titleCaseSegment).join(" ");
  return { cityName, stateCode };
};

// Find the tenant's subscribed city matching a name + state (case-insensitive).
export const findSubscribedCity = (
  cities: SubscribedCityLike[] | undefined,
  cityName: string,
  stateCode: string
): SubscribedCityLike | undefined =>
  (cities || []).find(
    (c) =>
      c.name?.toLowerCase() === cityName.toLowerCase() &&
      c.stateCode?.toLowerCase() === stateCode.toLowerCase()
  );

// Lock only when the tenant HAS subscriptions and this city isn't one of them.
// An empty/absent list (no subscriptions yet / mid-migration) is treated as
// "don't lock" so a tenant's landing pages don't all 404 before its cities are
// populated.
export const isCityLocked = (
  cities: SubscribedCityLike[] | undefined,
  cityName: string,
  stateCode: string
): boolean =>
  (cities?.length ?? 0) > 0 && !findSubscribedCity(cities, cityName, stateCode);
